



















































import { mapGetters } from 'vuex';
import { DataTableHeader } from 'vuetify';

import { AxiosError } from 'axios';
import {
  getAiStreamerUcGamesV2,
  toggleSupportAiStreamerUcGamesV2
} from '@/api/CopyStake/AiStreamer';
import { TAiStreamerUcGameItem } from '@/api/schema';
import { errorToastMessage } from '@/helpers/errorToastMessage';

interface IFilterData {
  searchBy: string;
  searchPhrase: string;
}

const defaultOptions = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['createdAt'],
  sortDesc: [true]
};

const CopyStakeAiStreamerGamesFilter = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeAiStreamer/GamesForAiStreamer/CopyStakeAiStreamerGamesFilter.vue'
  );

export default {
  name: 'CopyStakeGamesForAiStreamerTab',
  components: {
    CopyStakeAiStreamerGamesFilter
  },
  data(): any {
    return {
      gamesForAiStreamerList: [] as TAiStreamerUcGameItem[],
      gamesForAiStreamerListFilters: null,
      selectedItems: [] as TAiStreamerUcGameItem[],

      total: 0,
      options: { ...defaultOptions },
      loading: {
        list: false,
        toggle: {} as Record<number, boolean>,
        bulk: false
      }
    };
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    headers(): DataTableHeader[] {
      return [
        { text: 'Game Name', value: 'gameName', sortable: false },
        { text: 'Provider', value: 'provider', sortable: false },
        {
          text: 'AI Streamer Support',
          value: 'supported',
          width: 180,
          sortable: false
        },
        { text: 'Date Added (UTC)', value: 'createdAt', width: 200 }
      ];
    },
    selectLabels(): string[] {
      return ['game', 'games', 'games'];
    },
    hasUnsupportedSelected(): boolean {
      return this.selectedItems.some(
        (game: TAiStreamerUcGameItem) => !game.supported
      );
    },
    hasSupportedSelected(): boolean {
      return this.selectedItems.some(
        (game: TAiStreamerUcGameItem) => game.supported
      );
    }
  },
  watch: {
    operatorId: {
      handler: 'getGamesForAiStreamerList'
    }
  },
  methods: {
    handleUpdateFilter(filters: IFilterData | null): void {
      this.gamesForAiStreamerListFilters = filters || null;
      this.options.page = 1;

      this.getGamesForAiStreamerList();
    },
    async getGamesForAiStreamerList(): Promise<void> {
      if (!this.operatorId) return;

      this.loading.list = true;

      const filters = this.gamesForAiStreamerListFilters
        ? {
            [this.gamesForAiStreamerListFilters.searchBy]:
              this.gamesForAiStreamerListFilters.searchPhrase
          }
        : null;
      const params = {
        size: this.options.itemsPerPage,
        page: this.options.page - 1,
        sort: this.options.sortBy[0],
        order: this.options.sortDesc[0] ? 'DESC' : 'ASC'
      };

      return getAiStreamerUcGamesV2({ ...params, ...filters })
        .then(({ content, totalElements }) => {
          this.gamesForAiStreamerList = content;
          this.total = totalElements;
        })
        .catch((error: AxiosError) => {
          errorToastMessage(error);
          this.gamesForAiStreamerList = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading.list = false;
        });
    },
    async handleToggle(
      game: TAiStreamerUcGameItem,
      enabled: boolean
    ): Promise<void> {
      this.$set(this.loading.toggle, game.gameId, true);

      try {
        await toggleSupportAiStreamerUcGamesV2([game.gameId], enabled);
        game.supported = enabled;
        const action = enabled
          ? 'enabled for AI Streamer Support'
          : 'disabled for AI Streamer Support';
        this.$toast.success(`"${game.gameName}" was successfully ${action}`);
      } catch (error) {
        errorToastMessage(error);
        await this.getGamesForAiStreamerList();
      } finally {
        this.$delete(this.loading.toggle, game.gameId);
      }
    },
    async handleBulkEnable(): Promise<void> {
      const targetGames = this.selectedItems.filter(
        (game: TAiStreamerUcGameItem) => !game.supported
      );
      if (!targetGames.length) return;

      await this.processBulk(targetGames, true);
    },
    async handleBulkDisable(): Promise<void> {
      const targetGames = this.selectedItems.filter(
        (g: TAiStreamerUcGameItem) => g.supported
      );
      if (!targetGames.length) return;

      await this.processBulk(targetGames, false);
    },
    async processBulk(
      targetGames: TAiStreamerUcGameItem[],
      enable: boolean
    ): Promise<void> {
      this.loading.bulk = true;

      try {
        await toggleSupportAiStreamerUcGamesV2(
          targetGames.map((game) => game.gameId),
          enable
        );
        const count = targetGames.length;
        const noun = count > 1 ? 'games were' : 'game was';
        const action = enable
          ? 'enabled for AI Streamer Support'
          : 'disabled for AI Streamer Support';

        this.$toast.success(`${count} ${noun} successfully ${action}`);
        this.selectedItems = [];

        await this.getGamesForAiStreamerList();
      } catch (error) {
        errorToastMessage(error);
        this.selectedItems = [];
        await this.getGamesForAiStreamerList();
      } finally {
        this.loading.bulk = false;
      }
    }
  }
};
